import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Download, Loader2, FileSpreadsheet } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { inputCls, Field } from "@/components/AuthShell";
import { api, errMsg } from "@/lib/api";
import { COUNTRIES } from "@/lib/countries";

const toCsv = (rows) => "\ufeff" + ["الاسم,اسم الدخول,البريد,المستوى,كلمة المرور", ...rows.map((r) => [r.name, r.username, r.email, r.level, r.password].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))].join("\n");

const download = (name, text) => {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  a.click();
  URL.revokeObjectURL(url);
};

const TEMPLATE = "\ufeffالاسم,اسم الدخول,البريد,المستوى,كلمة المرور,الهاتف\nسارة أحمد,sara.ahmed,,1,,\nعمر خالد,omar.khaled,omar@school.com,2,Omar@2026,0500000000\n";

const Result = ({ res }) => (
  <div className="space-y-3" data-testid="import-result">
    <p className="text-sm font-semibold text-emerald-800" data-testid="import-created-count">تم إنشاء {res.created.length} حساب طالب مفعّل لمدة سنة.</p>
    {res.created.length > 0 && (
      <button onClick={() => download("yallahero-students.csv", toCsv(res.created))} data-testid="import-download-credentials"
        className="inline-flex items-center gap-2 rounded-full bg-[#047857] text-white px-5 py-2 text-sm font-semibold hover:bg-[#065F46]">
        <Download className="w-4 h-4" /> تنزيل بيانات الدخول (الأسماء وكلمات المرور)
      </button>
    )}
    {res.skipped.length > 0 && (
      <div className="border border-amber-300 bg-amber-50 p-3 text-xs max-h-40 overflow-y-auto" data-testid="import-skipped">
        <p className="font-semibold mb-1">لم يُضف {res.skipped.length} صف:</p>
        {res.skipped.map((s) => <p key={`${s.row}-${s.email}`} dir="auto">صف {s.row}: {s.email || "—"} · {s.reason}</p>)}
      </div>
    )}
  </div>
);

export const ImportStudentsDialog = ({ open, onOpenChange, onDone }) => {
  const [file, setFile] = useState(null);
  const [classId, setClassId] = useState("");
  const [school, setSchool] = useState("");
  const [country, setCountry] = useState("");
  const [classes, setClasses] = useState([]);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  useEffect(() => {
    if (!open) return;
    setFile(null); setRes(null); setClassId(""); setSchool(""); setCountry("");
    api.get("/admin/classes").then((r) => setClasses(Array.isArray(r.data) ? r.data : [])).catch(() => setClasses([]));
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("class_id", classId);
    fd.append("school", school.trim());
    fd.append("country", country);
    try {
      const { data } = await api.post("/admin/users/import", fd);
      setRes({ created: data?.created ?? [], skipped: data?.skipped ?? [] });
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="max-w-[94vw] sm:max-w-lg bg-[#FAF7F2] max-h-[90vh] overflow-y-auto" data-testid="import-students-dialog">
        <DialogHeader className="text-right sm:text-right">
          <DialogTitle className="font-display text-2xl">إضافة طلاب دفعة واحدة</DialogTitle>
          <DialogDescription>ارفع ملف Excel أو CSV فيه الأعمدة: الاسم، اسم الدخول، المستوى (1-4)، والبريد وكلمة المرور والهاتف اختيارية (يُولّد اسم دخول لمن لا اسم له). تُنشأ الحسابات مفعّلة لمدة سنة، وتُولَّد كلمة مرور لمن لا كلمة له.</DialogDescription>
        </DialogHeader>
        <button type="button" onClick={() => download("yallahero-template.csv", TEMPLATE)} data-testid="import-template-btn"
          className="self-start inline-flex items-center gap-2 text-sm font-semibold text-[#8B3A2B] hover:underline">
          <FileSpreadsheet className="w-4 h-4" /> تنزيل نموذج الملف
        </button>
        {res ? <Result res={res} /> : (
          <form onSubmit={submit} className="space-y-4">
            <Field label="الملف">
              <input type="file" accept=".csv,.xlsx" required onChange={(e) => setFile(e.target.files?.[0] ?? null)} className={inputCls} data-testid="import-file-input" />
            </Field>
            <Field label="إضافتهم إلى صف (اختياري)">
              <select value={classId} onChange={(e) => setClassId(e.target.value)} className={inputCls} data-testid="import-class-select">
                <option value="">بدون صف</option>
                {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="اسم المدرسة (اختياري)">
                <input maxLength={150} value={school} onChange={(e) => setSchool(e.target.value)} className={inputCls} data-testid="import-school-input" />
              </Field>
              <Field label="الدولة (اختياري)">
                <select value={country} onChange={(e) => setCountry(e.target.value)} className={inputCls} data-testid="import-country-select">
                  <option value="">بدون دولة</option>
                  {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </Field>
            </div>
            <button disabled={busy || !file} data-testid="import-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#8B3A2B] text-white py-3 font-semibold hover:bg-[#702E22] disabled:opacity-50">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />} رفع وإنشاء الحسابات
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
